/**
 * /erp/inventory — inventory shell with sub-tabs (Manager+).
 *
 * The inventory module has two views in v1:
 *
 *   * /erp/inventory               — current stock levels (per-warehouse)
 *   * /erp/inventory/adjustments   — manager-only audit trail of corrections
 *
 * This shell renders a tab bar across the top so the user can flip
 * between the two without losing context. Per the PRD's role split,
 * the "Adjustments" tab is the manager-only entry point — sales never
 * sees /erp/* in v1 at all (PR-A scope), but the role gate is centralised
 * here for safety.
 */

import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { Boxes } from "lucide-react";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/erp/inventory")({
  component: InventoryShell,
});

interface TabSpec {
  href: string;
  label: string;
  exact?: boolean;
}

const TABS: TabSpec[] = [
  { href: "/erp/inventory", label: "Stock levels", exact: true },
  { href: "/erp/inventory/adjustments", label: "Adjustments" },
];

function InventoryShell() {
  const { pathname } = useLocation();
  const isProduction = useAuthStore((state) => state.user?.role === "production");
  const tabs = isProduction ? TABS.filter((tab) => tab.exact) : TABS;

  const isActive = (tab: TabSpec) => {
    if (tab.exact) return pathname === tab.href;
    return pathname === tab.href || pathname.startsWith(`${tab.href}/`);
  };

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Boxes className="size-5 opacity-70" aria-hidden />
          Inventory
        </h1>
        <p className="text-sm opacity-60">
          Per-warehouse stock levels. Every adjustment requires a reason and is audit-logged.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-[var(--border)]" aria-label="Inventory sections">
        {tabs.map((tab) => (
          <Link
            key={tab.href}
            to={tab.href}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              isActive(tab)
                ? "border-[var(--accent)] text-[var(--accent)]"
                : "border-transparent opacity-70 hover:opacity-100"
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
